/**
 * S-06 Onboarding — identity (V1 auth-profiles-gdpr, PRD §3 model).
 * Gender (woman / man / nonbinary) + optional self-describe (≤30, display
 * only), pronouns, "show me" multi-select (≥1). The woman-first consequence
 * is explained transparently (©onboard.identity_note).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GENDERS, copy, type Gender } from "@humble/shared";
import { Button, Card } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { OnboardingFrame } from "./onboarding-frame.js";
import "./onboarding.css";

const SHOW_ME_LABELS: Record<Gender, string> = {
  woman: "Women",
  man: "Men",
  nonbinary: "Nonbinary people",
};

export function IdentityScreen() {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState<Gender | null>(null);
  const [selfDescribe, setSelfDescribe] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [showMe, setShowMe] = useState<Set<Gender>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleShowMe(g: Gender) {
    setShowMe((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }

  async function onContinue() {
    if (busy) return;
    if (!displayName.trim()) {
      setError("You need a name people can see. A first name, a handle, a warning label — your call.");
      return;
    }
    if (!gender) {
      setError("Pick a gender — the deck needs to know who to show you to.");
      return;
    }
    if (showMe.size === 0) {
      setError("Pick at least one option under “Show me”.");
      return;
    }
    if (selfDescribe.length > 30) {
      setError("Self-describe labels max out at 30 characters.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put("/onboarding/identity", {
        body: {
          display_name: displayName.trim(),
          gender,
          ...(selfDescribe ? { gender_self_describe: selfDescribe } : {}),
          ...(pronouns ? { pronouns } : {}),
          show_me: [...showMe],
        },
      });
      navigate("/onboard/profile");
    } catch (err) {
      setError(isApiError(err) ? err.message : "Network error — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OnboardingFrame step={1} screen="S-06">
      <Card padding="lg">
        <p className="h-caps onboard__kicker">Step 1 of 4</p>
        <h1 className="type-display-3 onboard__title">Who are you?</h1>

        <div className="field">
          <label htmlFor="identity-display-name">Display name</label>
          <input
            id="identity-display-name"
            data-testid="identity-display-name"
            type="text"
            maxLength={40}
            autoComplete="nickname"
            placeholder="What should people call you?"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        <p style={{ fontWeight: 600, margin: "8px 0 4px" }}>I am a…</p>
        <div className="identity__options" role="radiogroup" aria-label="Gender">
          {GENDERS.map((g) => (
            <label key={g} className={`identity__option${gender === g ? " identity__option--on" : ""}`}>
              <input
                type="radio"
                name="gender"
                value={g}
                checked={gender === g}
                onChange={() => setGender(g)}
              />
              {g === "nonbinary" ? "Nonbinary person" : g[0]!.toUpperCase() + g.slice(1)}
            </label>
          ))}
        </div>

        <div className="field">
          <label htmlFor="identity-self-describe">In my own words (optional)</label>
          <input
            id="identity-self-describe"
            type="text"
            maxLength={30}
            placeholder="Displayed on your profile, never used for matching"
            value={selfDescribe}
            onChange={(e) => setSelfDescribe(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="identity-pronouns">Pronouns (optional)</label>
          <input
            id="identity-pronouns"
            type="text"
            maxLength={30}
            placeholder="e.g. she/her, he/him, they/them"
            value={pronouns}
            onChange={(e) => setPronouns(e.target.value)}
          />
        </div>

        <p style={{ fontWeight: 600, margin: "10px 0 4px" }}>Show me…</p>
        <div className="identity__options" role="group" aria-label="Show me">
          {GENDERS.map((g) => (
            <label key={g} className={`identity__option${showMe.has(g) ? " identity__option--on" : ""}`}>
              <input type="checkbox" checked={showMe.has(g)} onChange={() => toggleShowMe(g)} />
              {SHOW_ME_LABELS[g]}
            </label>
          ))}
        </div>

        <p className="type-editorial text-dim" style={{ fontSize: "1rem" }}>
          {copy.onboard.identity_note}
        </p>

        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
        <div className="onboard__actions">
          <Button block variant="primary" size="lg" loading={busy} onClick={() => void onContinue()}>
            Continue
          </Button>
        </div>
      </Card>
    </OnboardingFrame>
  );
}
